import * as Sentry from '@sentry/react';
import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { TDR } from 'tdr-common';
import { useCancelReservation } from '../../api/cancelReservation';
import { useDeletePendingMods } from '../../api/deletePendingModifications';
import { useReservation } from '../../api/getReservation';
import { useRestaurant } from '../../api/getRestaurant';
import { useRestaurantBasePolicies } from '../../api/getRestaurantBasePolicies';
import { useFeatureFlags } from '../../api/getRestaurantFeatureFlags';
import { useRestaurantTaxPolicies } from '../../api/getRestaurantTaxPolicies';
import { useTable } from '../../api/getTable';
import { URLParams } from '../../common/types';
import Spinner from '../../components/Spinner';
import { CTAButton, CustomerSupportLink } from '../../components/buttons';
import { Header } from '../../components/checkout/Header';
import { ConfirmationDialog } from '../../components/dialog/confirmation-dialog/ConfirmationDialog';
import { AddOns } from '../../components/modifications/AddOns';
import { BookingDetails } from '../../components/modifications/BookingDetails';
import { GuestDetails } from '../../components/modifications/GuestDetails';
import { Occasion } from '../../components/modifications/Occasion';
import { SpecialRequests } from '../../components/modifications/SpecialRequests';
import { PrivateEventDetails } from '@/components/modifications/PrivateEventDetails';
import { useModificationFlowState } from '../../context/ModificationFlowProvider';
import { getNewInvoiceForCancellation } from '../../helpers/getNewInvoiceForCancellation';
import { trackReservationCancelled } from '../../helpers/reservationEventTrackers';
import TablzLogo from '../../images/poweredBy/tablz.svg';
import layoutStyles from '../../layouts/ModificationLayout.module.scss';
import { ResultPageLayout } from '../../layouts/ResultPageLayout';
import { ModifyBookingFormData } from '../../routes/BookingGuard';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

const MAX_RETRIES = 10;

export function ManageBookingPage() {
	const { t, i18n } = useTranslation();
	const navigate = useNavigate();
	const { reservationId } = useParams<URLParams>();

	const [refetchCount, setRefetchCount] = useState(0);
	const [refetchError, setRefetchError] = useState(false);

	const {
		startFlow,
		setNewInvoice,
		isPastModificationPeriod,
		isPastRefundablePeriod,
		setNonRefundableWarning,
		setCannotModifyWarning
	} = useModificationFlowState();

	const methods = useFormContext<ModifyBookingFormData>();

	const cancelReservationMutation = useCancelReservation(reservationId);
	const deletePendingModsMutation = useDeletePendingMods(reservationId);

	const { data: reservation, refetch: refetchReservation } = useReservation(reservationId);
	const { data: restaurant } = useRestaurant(reservation.restaurantId);
	const { data: table } = useTable(reservation.tableId);
	const { data: restaurantPolicies } = useRestaurantBasePolicies(restaurant.id);
	const { data: taxPolicies } = useRestaurantTaxPolicies(restaurant);
	const { data: featureFlags } = useFeatureFlags(reservation.restaurantId);

	const addOns = reservation.items.filter((item) => item.type === 'addon');

	const clearNewInvoice = () => {
		setNewInvoice(undefined);
	};

	useEffect(() => {
		startFlow();
	}, [startFlow]);

	useEffect(() => {
		let interval: NodeJS.Timeout;

		if (reservation.status === TDR.Reservation.Status.Pending) {
			// Delete any pending changes and refetch reservation (in case user dropped off with pending changes while previously modifying their booking)
			if (reservation['expirationTask'] && reservation.pendingChanges) {
				deletePendingModsMutation.mutateAsync(reservationId).then(() => {
					refetchReservation();
					clearNewInvoice();
				});
			}

			// Or, we may be waiting for a stripe webhook response, refetch the reservation every 2s (up to MAX_RETRIES) to get the updated booking details
			else {
				interval = setInterval(() => {
					if (refetchCount < MAX_RETRIES) {
						refetchReservation();
						setRefetchCount((count) => count + 1);
					}
					else {
						setRefetchError(true);
						clearInterval(interval);
					}
				}, 2000);
			}
		}

		return () => clearInterval(interval);
	}, [reservation, refetchCount, refetchReservation]);

	const confirmCancellationBody = (
		<>
			{isPastRefundablePeriod ||
				(isPastModificationPeriod && (
					<span>
						{t('messages.non_refundable_note_with_restaurant', {
							restaurantName: restaurant.displayName || restaurant.name
						})}
					</span>
				))}
			<span>{t('messages.action_irreversible')}</span>

			<p>
				{t('messages.contact_support_prefix')} <CustomerSupportLink /> {t('messages.contact_support_suffix')}
			</p>
		</>
	);

	const handleCancellation = async () => {
		const cancellationInvoice = await getNewInvoiceForCancellation({
			restaurant,
			table,
			reservation,
			basePolicies: [...(restaurantPolicies ?? []), ...(taxPolicies ?? [])]
		});
		const response = await cancelReservationMutation.mutateAsync({ cancellationInvoice });

		if (response.success) {
			trackReservationCancelled(reservation);
			navigate(`/booking/${reservationId}/cancel-success`);
		}
		else {
			const errorMessage = `cancelReservation Error: ${response.message}`;
			const sentryErrorID = Sentry.captureException(new Error(errorMessage));
			trackReservationCancelled(reservation, errorMessage, sentryErrorID);
			toast.error(t('errors.oops_something_went_wrong'));
		}
	};

	const checkCutoffs = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
		event.preventDefault();
		const pathname = event.currentTarget.pathname;

		if (isPastModificationPeriod) {
			setCannotModifyWarning({ isOpen: true });
		}
		else if (isPastRefundablePeriod) {
			setNonRefundableWarning({ isOpen: true, proceedTo: pathname });
		}
		else {
			navigate(pathname);
		}
	};

	if (refetchError) {
		toast.error(t('errors.loading_booking_failed'), {
			toastId: 'refetch-error',
			autoClose: false
		});
	}

	if (cancelReservationMutation.isPending) {
		return <LoadingStep key={i18n.language} message={t('messages.cancelling_booking')} />;
	}

	if (reservation.status === TDR.Reservation.Status.Pending || deletePendingModsMutation.status === 'pending') {
		return <LoadingStep key={i18n.language} message={t('messages.loading_booking_details')} />;
	}

	const cancelButton = 'm-0 p-0 flex justify-center items-center h-14 w-full bg-[#7A271A] border border-tablz-med-gray-4 rounded-xl cursor-pointer font-semibold text-sm font-poppins uppercase tracking-wider leading-none text-[#FEE4E2]';

	return (
		<div className="w-full h-full overflow-hidden flex flex-col justify-between">
			<div className="fixed sm:static top-0 left-0 right-0 z-20 bg-tablz-dark-gray-2">
				<Header title={t('titles.manage_booking')} subtitle={restaurant.displayName || restaurant.name} iconLeft={<></>} />
			</div>

			<div className="flex-1 overflow-y-scroll overflow-x-hidden flex flex-col gap-2 py-20 sm:p-0 bg-tablz-dark-gray-2">
				<BookingDetails
					table={table}
					restaurant={restaurant}
					reservation={reservation}
					checkCutoffs={checkCutoffs}
				/>

				{featureFlags?.enableAddOns && <AddOns addOns={addOns} restaurant={restaurant} />}

				<GuestDetails reservation={reservation} />

				{reservation.isLargeGroup && (
					<Occasion isEditEnabled={!isPastModificationPeriod} reservation={reservation} form={methods} />
				)}

				<SpecialRequests isEditEnabled={!isPastModificationPeriod} reservation={reservation} form={methods} />

				{reservation?.companyName || reservation?.eventDuration || reservation?.estimatedBudget ? <PrivateEventDetails
					companyName={reservation.companyName}
					eventDuration={reservation.eventDuration}
					estimatedBudget={reservation.estimatedBudget}
				/> : null}
			</div>

			<div className={cn('flex flex-col static gap-3 p-2 bottom-0 left-0 right-0 z-20 border-t border-tablz-med-gray-4 bg-tablz-dark-gray-2 [&>button]:p-2 [&>button]:text-decoration-none', layoutStyles.Footer)}>
				{isPastModificationPeriod ? (
					<p className={cancelButton} onClick={() => setCannotModifyWarning({ isOpen: true })}>
						{t('actions.cancel_booking', { defaultValue: 'Cancel Booking' })}
					</p>
				) : (
					<ConfirmationDialog
						icon='danger'
						title={t('titles.cancel_booking_question')}
						body={confirmCancellationBody}
						triggerButton={<p className={cancelButton}>{t('actions.cancel_booking', { defaultValue: 'Cancel Booking' })}</p>}
						confirmButton={
							<CTAButton
								onClick={handleCancellation}
								variant='transparent'
								buttonText={t('actions.cancel_booking', { defaultValue: 'Cancel Booking' })}
								backgroundColor='#7A271A'
							/>
						}
						cancelButtonText={t('actions.return_to_booking_details', { defaultValue: 'Return to Booking Details' })}
						isDone={cancelReservationMutation.isSuccess}
					/>
				)}
			</div>
		</div>
	);
}

const LoadingStep = ({ message }: { message: string }) => {
	return (
		<ResultPageLayout>
			<img src={TablzLogo} />

			<ResultPageLayout.Title>{message}</ResultPageLayout.Title>

			<ResultPageLayout.Body>
				<Spinner size='xs' />
			</ResultPageLayout.Body>
		</ResultPageLayout>
	);
};
